<!--
  CAPTCHA Component
  =================

  Uses a Google reCAPTCHA Enterprise policy-based challenge key.

  How it works:
  - On mount, loads the reCAPTCHA Enterprise script and auto-executes
  - For trusted users (high score): passes silently, form auto-submits
  - For suspicious users (low score): Google automatically shows a visual challenge
  - After verification, the token is populated into the form's hidden input
    and the form is auto-submitted

  Props (via spec object):
  - site_key: Google reCAPTCHA Enterprise site key (required)
-->

<template>
  <div>
    <!-- Configuration error state -->
    <div v-if="!siteKey" class="text-red-600 p-4 bg-red-50 rounded-lg">
      <p class="font-semibold">Configuration Error</p>
      <p>reCAPTCHA Enterprise site key is not configured. Please contact support.</p>
    </div>

    <!-- Normal CAPTCHA component -->
    <div v-else>
      <p v-if="loading" class="text-sm text-gray-500">Verifying...</p>
      <p v-if="error" class="mt-2 text-sm text-red-600">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import Action from "glib-web/action";

export default {
  props: {
    spec: { type: Object, required: true },
  },

  data: function () {
    return {
      siteKey: this.spec.siteKey || null,
      loading: true,
      error: null
    }
  },

  mounted: function () {
    if (this.siteKey) {
      this.initRecaptcha()
    }
  },

  methods: {
    loadRecaptchaEnterprise: function () {
      var self = this
      return new Promise(function (resolve) {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          resolve(window.grecaptcha.enterprise)
          return
        }

        window.onRecaptchaEnterpriseLoad = function () {
          resolve(window.grecaptcha.enterprise)
        }

        var script = document.createElement('script')
        script.src = 'https://www.google.com/recaptcha/enterprise.js?render=' + self.siteKey + '&onload=onRecaptchaEnterpriseLoad'
        script.async = true
        script.defer = true
        document.head.appendChild(script)
      })
    },

    findTokenInput: function () {
      var inputs = document.querySelectorAll('input[name="g-recaptcha-response"]')
      return inputs.length > 0 ? inputs[0] : null
    },

    initRecaptcha: async function () {
      try {
        var enterprise = await this.loadRecaptchaEnterprise()
        var self = this

        enterprise.ready(function () {
          enterprise.execute(self.siteKey, { action: 'VERIFY' }).then(function (token) {
            if (!token) {
              self.error = 'Verification failed. Please refresh and try again.'
              self.loading = false
              return
            }

            self.loading = false

            var tokenInput = self.findTokenInput()
            if (tokenInput) {
              tokenInput.value = token
              tokenInput.dispatchEvent(new Event('input', { bubbles: true }))
              tokenInput.dispatchEvent(new Event('change', { bubbles: true }))
            }

            // Auto-submit the form via the onSolve action
            Action.execute(self.spec.onSolve, self)
          }).catch(function (err) {
            console.error('reCAPTCHA execute failed:', err)
            self.error = 'Verification failed. Please refresh and try again.'
            self.loading = false
          })
        })
      } catch (e) {
        console.error('Failed to load reCAPTCHA:', e)
        this.error = 'Failed to load verification. Please refresh and try again.'
        this.loading = false
      }
    }
  }
}
</script>
